/* eslint react/display-name: 0, react/no-multi-comp: 0 */
import React, { Component, Fragment, ReactNode } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
// Until we update our tsconfig target we don't have optional chaining
import get from 'lodash/get'
import { Icon } from '@scentregroup/ui'
import algoliasearch, { SearchClient } from 'algoliasearch'
import { CONFIG_KEYS, getClientConfigValue } from '../../helpers/config-helper'
import { distancePerState } from '../../helpers/distance-per-state'
import analyticsUtils from '../../helpers/analytics-utils'
import { getNowInCentreInTimeFormat } from '../../helpers/date-helpers'
import { VIEW_OPTION_MEMBERS_ONLY } from '../../constants'
import { Centre } from '../../../types/contentful-v1'
import { FeatureFlagsContext } from '../../../shared/context'
import { FeatureFlags } from '../../../shared/feature-flags'
import SearchResult from './search-result'
import { CentreType } from './types'
import SearchView from './search-view'
import style from './index.scss'

const HEADINGS = {
  products: 'Products',
  services: 'Centre services',
  topStores: 'Top Stores',
  events: 'Events',
  offers: 'Offers',
  stories: 'Story',
  news: 'News',
  centres: 'Centres',
  nearbyStores: 'Other nearby stores',
  otherStores: 'Other stores near',
}

type Index =
  | 'products_au'
  | 'store'
  | 'site'
  | 'service'
  | 'offer'
  | 'event'
  | 'news'
  | 'story'
  | 'centre'

// Expected shape of Algolia hits for various indexes:

interface AlgoliaHit {
  objectID: string
}

interface ProductHit extends AlgoliaHit {
  name: string
  shop: { id: string; name: string }
  description?: string
  store?: string
  image?: string
  variant?: {
    price?: { effectivePrice?: number }
  }
  variantGroupCode?: string
}

interface ContentfulHit extends AlgoliaHit {
  slug: string
  img: string
  title: string
}

interface PageHit extends ContentfulHit {
  href: string
}

interface StoreHit extends ContentfulHit {
  centreSlug: string
  centreName: string
}

interface OfferHit extends ContentfulHit {
  storeTitles: string[]
}

interface PostHit extends ContentfulHit {
  shortDescription: string
}

type Hit<T extends Index> = T extends 'products_au'
  ? ProductHit
  : T extends 'store'
  ? StoreHit
  : T extends 'site' | 'centre'
  ? PageHit
  : T extends 'offer'
  ? OfferHit
  : T extends 'event' | 'story' | 'news'
  ? PostHit
  : ContentfulHit

// Placeholder until the relevant type from algoliasearch can be imported:
interface SearchParams {
  filters?: string
  aroundLatLng?: string
  facetFilters?: string
  aroundRadius?: number
}

interface Query<T extends Index> {
  indexName: T
  query: string
  params?: SearchParams
}

interface Section<T extends Index> extends Query<T> {
  heading: string
  render: React.FC<Hit<T>>
}

type ContentfulSections = [
  Section<'store'>, // Same centre stores
  Section<'site'>,
  Section<'service'>,
  Section<'offer'>,
  Section<'event'>,
  Section<'news'>,
  Section<'story'>,
  Section<'centre'>,
  Section<'store'> // Other centre stores
]

type MarketplaceSections = [Section<'products_au'>]

type AllSections = Section<Index>[]

interface RawResults {
  hits: AlgoliaHit[]
}

// Parametrically typing this and getting it to match the type of the Sections is uurgh
interface SectionResults extends RawResults {
  heading: string
}

interface RenderedSectionResults {
  heading: string
  hits: ReactNode[]
}

interface SearchProps {
  className: string
  centre: Centre
  isWevo?: boolean
}

const ESCAPE_CODE = 'Escape'

const hrefForStore = (centreSlug: string, store: StoreHit): string =>
  `/${centreSlug}/store/${store.objectID}/${store.slug}`

const descriptionForProduct = (hit: ProductHit): string => {
  const price = get(hit, ['variant', 'price', 'effectivePrice'])
  const priceDecimals = 2
  const prefix = Number.isFinite(price)
    ? `$${price.toFixed(priceDecimals)}`
    : ''
  const suffix = get(hit, ['shop', 'name'], '')
  const infix = prefix.length > 0 && suffix.length > 0 ? ' from ' : ''
  return `${prefix}${infix}${suffix}`
}

// The queries we run whenever text changes, and how the results turn into dom.
// Everything here is passed to algolia except for the render method.

const centreWithDateAndViewOptionFilter = (
  centreID: string,
  viewOptionToExclude: string,
  getOffsetlessTimeInCentre: () => number
): SearchParams => ({
  filters: `centres:${centreID} AND (NOT viewOption:"${viewOptionToExclude}") AND (showAt<${getOffsetlessTimeInCentre()} AND hideAt>${getOffsetlessTimeInCentre()})`,
})

const singleCentreWithDateFilter = (
  centreID: string,
  getOffsetlessTimeInCentre: () => number
): SearchParams => ({
  filters: `centre:${centreID} AND (showAt<${getOffsetlessTimeInCentre()} AND hideAt>${getOffsetlessTimeInCentre()})`,
})

const getContentfulSections = (
  query: string,
  centre: Centre,
  onResultClick: (heading: string, hit: ContentfulHit) => void,
  getOffsetlessTimeInCentre: () => number
): ContentfulSections => [
  {
    heading: HEADINGS.topStores,
    indexName: 'store',
    query,
    params: singleCentreWithDateFilter(
      centre.sys.id,
      getOffsetlessTimeInCentre
    ),
    render: hit => (
      <SearchResult
        key={hit.objectID}
        href={hrefForStore(centre.slug, hit)}
        imageUrl={hit.img}
        highlightField="titleWithOptionalStatus"
        hit={hit}
        className={style.storeRow}
        onClick={() => onResultClick(HEADINGS.topStores, hit)}
      />
    ),
  },
  {
    heading: HEADINGS.services,
    indexName: 'site',
    query,
    render: hit => (
      <SearchResult
        key={hit.objectID}
        href={(hit.href || '').replace('CENTRE', centre.slug)}
        imageUrl={hit.img}
        highlightField="text"
        hit={hit}
        onClick={() => onResultClick(HEADINGS.services, hit)}
      />
    ),
  },
  {
    heading: HEADINGS.services,
    indexName: 'service',
    query,
    params: singleCentreWithDateFilter(
      centre.sys.id,
      getOffsetlessTimeInCentre
    ),
    render: hit => (
      <SearchResult
        key={hit.objectID}
        href={`/${centre.slug}/service/${hit.objectID}/${hit.slug}`}
        imageUrl={hit.img}
        highlightField="title"
        hit={hit}
        onClick={() => onResultClick(HEADINGS.services, hit)}
      />
    ),
  },
  {
    heading: HEADINGS.offers,
    indexName: 'offer',
    query,
    params: centreWithDateAndViewOptionFilter(
      centre.sys.id,
      VIEW_OPTION_MEMBERS_ONLY,
      getOffsetlessTimeInCentre
    ),
    render: hit => (
      <SearchResult
        key={hit.objectID}
        href={`/${centre.slug}/offer/${hit.objectID}/${hit.slug}`}
        imageUrl={hit.img}
        highlightField="title"
        hit={hit}
        description={hit.storeTitles.join(', ')}
        onClick={() => onResultClick(HEADINGS.offers, hit)}
      />
    ),
  },
  {
    heading: HEADINGS.events,
    indexName: 'event',
    query,
    params: centreWithDateAndViewOptionFilter(
      centre.sys.id,
      VIEW_OPTION_MEMBERS_ONLY,
      getOffsetlessTimeInCentre
    ),
    render: hit => (
      <SearchResult
        key={hit.objectID}
        href={`/${centre.slug}/event/${hit.objectID}/${hit.slug}`}
        imageUrl={hit.img}
        highlightField="title"
        hit={hit}
        description={hit.shortDescription}
        onClick={() => onResultClick(HEADINGS.events, hit)}
      />
    ),
  },
  {
    heading: HEADINGS.news,
    indexName: 'news',
    query,
    params: centreWithDateAndViewOptionFilter(
      centre.sys.id,
      VIEW_OPTION_MEMBERS_ONLY,
      getOffsetlessTimeInCentre
    ),
    render: hit => (
      <SearchResult
        key={hit.objectID}
        href={`/${centre.slug}/news/${hit.objectID}/${hit.slug}`}
        imageUrl={hit.img}
        highlightField="title"
        hit={hit}
        description={hit.shortDescription}
        onClick={() => onResultClick(HEADINGS.news, hit)}
      />
    ),
  },
  {
    heading: HEADINGS.stories,
    indexName: 'story',
    query,
    params: centreWithDateAndViewOptionFilter(
      centre.sys.id,
      VIEW_OPTION_MEMBERS_ONLY,
      getOffsetlessTimeInCentre
    ),
    render: hit => (
      <SearchResult
        key={hit.objectID}
        href={`/${centre.slug}/story/${hit.objectID}/${hit.slug}`}
        imageUrl={hit.img}
        highlightField="title"
        hit={hit}
        description={hit.shortDescription}
        onClick={() => onResultClick(HEADINGS.stories, hit)}
      />
    ),
  },
  {
    heading: HEADINGS.centres,
    indexName: 'centre',
    query,
    params: {
      filters: `country:"${centre.country}"`,
    },
    render: hit => (
      <SearchResult
        key={hit.objectID}
        href={hit.href}
        imageUrl={hit.img}
        highlightField="title"
        hit={hit}
        onClick={() => onResultClick(HEADINGS.centres, hit)}
      />
    ),
  },
  {
    heading: `${HEADINGS.otherStores} ${centre.title}`,
    indexName: 'store',
    query,
    params: {
      aroundLatLng: `${centre.latitude}, ${centre.longitude}`,
      facetFilters: `centre:-${centre.sys.id}`,
      aroundRadius: distancePerState(centre.state), // in meters
    },
    render: hit => (
      <SearchResult
        noImage
        key={hit.objectID}
        href={hrefForStore(hit.centreSlug, hit)}
        highlightField="title"
        hit={hit}
        description={hit.centreName}
        className={style.otherStoreRow}
        onClick={() =>
          onResultClick(`${HEADINGS.otherStores} ${centre.title}`, hit)
        }
      />
    ),
  },
]

const getMarketplaceSections = (
  query: string,
  onResultClick: (heading: string, hit: ProductHit) => void
): MarketplaceSections => [
  {
    heading: HEADINGS.products,
    indexName: 'products_au',
    query,
    render: hit => (
      <SearchResult
        imageUrl={hit.image}
        key={hit.objectID}
        href={`/shop/product/${hit.shop.id}/${hit.variantGroupCode}`}
        highlightField="name"
        hit={hit}
        description={descriptionForProduct(hit)}
        onClick={() => onResultClick(HEADINGS.products, hit)}
      />
    ),
  },
]

// Results:
// 1. Get rid of results with no hits
// 2. Merge results with the same header
// 3. Return the heading and render method
export const buildResults = (
  results: RawResults[],
  sections: AllSections
): RenderedSectionResults[] =>
  results.reduce(
    (
      acc: RenderedSectionResults[],
      { hits }: { hits: AlgoliaHit[] },
      i: number
    ) => {
      if (hits.length > 0) {
        const { render, heading } = sections[i]
        const matchingIndex = acc.findIndex(res => res.heading === heading)
        const newHits = hits.map(render as React.FC<AlgoliaHit>)

        if (matchingIndex >= 0) {
          acc[matchingIndex].hits = [...acc[matchingIndex].hits, ...newHits]
        } else {
          acc.push({ heading, hits: newHits })
        }
      }
      return acc
    },
    []
  )

// This is sort of a controller component which runs search queries against
// algolia and re-renders.
export default class Search extends Component<SearchProps> {
  static propTypes = {
    centre: CentreType,
    className: PropTypes.string,
  }
  constructor(props: SearchProps) {
    super(props)
    this.contentfulClient = null
    this.marketplaceClient = null
    this.getOffsetlessTimeInCentre = () => 0 // in practice this is always set on query text change
    if (
      !getClientConfigValue(CONFIG_KEYS.NEXT_PUBLIC_ALGOLIA_APPLICATION_ID) ||
      !getClientConfigValue(CONFIG_KEYS.NEXT_PUBLIC_ALGOLIA_SEARCH_KEY)
    ) {
      return
    }

    const customOrigin = getClientConfigValue(
      CONFIG_KEYS.NEXT_PUBLIC_ALGOLIA_CUSTOM_ORIGIN
    )
    // TODO would like to use URL but will it be cross browser supported / transpiled?
    let opts = {}
    if (customOrigin) {
      const [protocol, host] = customOrigin.split('//')
      const hosts = [host]
      opts = { hosts, protocol }
    }

    this.contentfulClient = algoliasearch(
      // It's fine to put these in the client.
      // https://www.algolia.com/doc/guides/security/api-keys/?language=php#search-only-api-key
      getClientConfigValue(CONFIG_KEYS.NEXT_PUBLIC_ALGOLIA_APPLICATION_ID),
      getClientConfigValue(CONFIG_KEYS.NEXT_PUBLIC_ALGOLIA_SEARCH_KEY),
      opts
    )
    if (
      !getClientConfigValue(CONFIG_KEYS.NEXT_PUBLIC_ALGOLIA_APPLICATION_ID) ||
      !getClientConfigValue(CONFIG_KEYS.NEXT_PUBLIC_ALGOLIA_SEARCH_KEY)
    ) {
      return
    }

    const NEXT_PUBLIC_MKP_ALGOLIA_APPLICATION_ID = getClientConfigValue(
      CONFIG_KEYS.NEXT_PUBLIC_MKP_ALGOLIA_APPLICATION_ID
    )
    const NEXT_PUBLIC_MKP_ALGOLIA_SEARCH_KEY = getClientConfigValue(
      CONFIG_KEYS.NEXT_PUBLIC_MKP_ALGOLIA_SEARCH_KEY
    )
    const hasMarketplaceKeys =
      NEXT_PUBLIC_MKP_ALGOLIA_APPLICATION_ID &&
      NEXT_PUBLIC_MKP_ALGOLIA_SEARCH_KEY
    const useMarketplaceClient = hasMarketplaceKeys && props.isWevo
    if (useMarketplaceClient) {
      this.marketplaceClient = algoliasearch(
        getClientConfigValue(
          CONFIG_KEYS.NEXT_PUBLIC_MKP_ALGOLIA_APPLICATION_ID
        ),
        getClientConfigValue(CONFIG_KEYS.NEXT_PUBLIC_MKP_ALGOLIA_SEARCH_KEY)
      )
    }
  }

  state = {
    nextClock: 1,
    visible: false,
    clock: 0,
    results: [], // List of sections. Each section has {heading, hits: [...]}.
    queryText: '',
  }

  private contentfulClient: SearchClient | null
  private marketplaceClient: SearchClient | null
  private getOffsetlessTimeInCentre: () => number

  _handleKeyDown = (event: KeyboardEvent): void => {
    switch (event.code) {
      case ESCAPE_CODE:
        this.hideSearch()
        break
      default:
        break
    }
  }

  setResults(clock: number, results: RenderedSectionResults[]): void {
    // The clock ensures we don't have race conditions from earlier search
    // results coming back after later ones
    if (clock > this.state.clock) {
      this.setState({
        clock,
        results,
      })
    }
  }

  onQueryChange = async (queryText: string): Promise<void> => {
    this.setState({ queryText })
    // Used to make sure we always render the last
    const clock = this.state.nextClock
    this.setState({ nextClock: this.state.nextClock + 1 })

    if (!this.contentfulClient || queryText.trim() === '') {
      // Show no results if the query text is deleted or there is no client.
      this.setResults(clock, [])
      return
    }

    const onResultClick = (
      heading: string,
      hit: ContentfulHit | ProductHit
    ): void => {
      analyticsUtils.gaTagTracking({
        event: analyticsUtils.events.SEARCH,
        eventCategory: analyticsUtils.categories.GLOBAL_SEARCH,
        eventAction: analyticsUtils.actions.SEARCH_RESULT_ClICKED,
        eventLabel: queryText,
        globalResultClicked: `${heading}/${
          (hit as ContentfulHit).title || (hit as ProductHit).name
        }`,
      })
    }
    this.getOffsetlessTimeInCentre = getNowInCentreInTimeFormat(
      this.props.centre.timeZone
    ) as () => number // TS won't infer this type for some reason

    const contentfulSections = getContentfulSections(
      queryText,
      this.props.centre,
      onResultClick,
      this.getOffsetlessTimeInCentre
    )
    const marketplaceSections = getMarketplaceSections(queryText, onResultClick)
    const makeQueries = (sections: Section<Index>[]): Query<Index>[] =>
      sections.map(section => {
        const { params, query, indexName } = section
        return { params, query, indexName }
      })
    const searches = [
      this.contentfulClient.search(makeQueries(contentfulSections)),
    ]
    if (this.marketplaceClient) {
      searches.push(
        this.marketplaceClient.search(makeQueries(marketplaceSections))
      )
    }
    const [contentfulContent, marketplaceContent] = await Promise.all(searches)
    const allSections = [...marketplaceSections, ...contentfulSections]
    const results = buildResults(
      [
        ...get(marketplaceContent, 'results', [{ hits: [] }]),
        ...contentfulContent.results,
      ],
      allSections
    )
    this.setResults(clock, results)
    const numHits = results.reduce(
      (acc: number, { hits }: { hits: ReactNode[] }) => acc + hits.length,
      0
    )
    analyticsUtils.gaTagTracking({
      event: analyticsUtils.events.SEARCH,
      eventCategory: analyticsUtils.categories.GLOBAL_SEARCH,
      eventAction: analyticsUtils.actions.SEARCH_SHOWN,
      eventLabel: this.state.queryText,
      noReturnedResults: numHits,
    })
  }

  hideSearch = (): void => {
    document.removeEventListener('keydown', this._handleKeyDown)
    window.scroll(0, 0)
    this.setState({ visible: false })
    analyticsUtils.gaTagTracking({
      event: analyticsUtils.events.SEARCH,
      eventCategory: analyticsUtils.categories.GLOBAL_SEARCH,
      eventAction: analyticsUtils.actions.CLICK_TO_CLOSE_SEARCH,
      eventLabel: this.state.queryText,
    })
  }

  showSearch = (): void => {
    document.addEventListener('keydown', this._handleKeyDown)
    this.setState({ visible: true })
    analyticsUtils.gaTagTracking({
      event: analyticsUtils.events.SEARCH,
      eventCategory: analyticsUtils.categories.GLOBAL_SEARCH,
      eventAction: analyticsUtils.actions.CLICK_TO_START_SEARCH,
    })
  }

  onQueryKeyDown = (e: KeyboardEvent): void => {
    if (e.code === ESCAPE_CODE) {
      // Escape cancels the search modal.
      this.hideSearch()
    }
  }

  onClickClear = (): void => {
    const clock = this.state.nextClock
    this.setState({
      queryText: '',
      nextClock: this.state.nextClock + 1,
      clock,
      results: [],
    })
    analyticsUtils.gaTagTracking({
      event: analyticsUtils.events.SEARCH,
      eventCategory: analyticsUtils.categories.GLOBAL_SEARCH,
      eventAction: analyticsUtils.actions.CLICK_TO_CLEAR_SEARCH_KEYWORD,
      eventLabel: this.state.queryText,
    })
  }

  render(): ReactNode {
    if (
      !getClientConfigValue(CONFIG_KEYS.NEXT_PUBLIC_ALGOLIA_APPLICATION_ID) ||
      !getClientConfigValue(CONFIG_KEYS.NEXT_PUBLIC_ALGOLIA_SEARCH_KEY)
    ) {
      return null
    }

    const { className, isWevo } = this.props

    return (
      <Fragment>
        <SearchView
          results={this.state.results}
          queryText={this.state.queryText}
          isVisible={this.state.visible}
          isWevo={isWevo}
          onQueryChange={this.onQueryChange}
          onQueryKeyDown={this.onQueryKeyDown}
          onClickClose={this.hideSearch}
          onClickClear={this.onClickClear}
        />
        <button
          aria-label="Search"
          className={classNames(className, style.searchButton)}
          data-test-id="centre-search"
          onClick={this.showSearch}
        >
          <Icon type="search" />
        </button>
      </Fragment>
    )
  }
}
